import React, { useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import api from "../../utils/axios";
import Swal from "sweetalert2";
import {
  Briefcase,
  UserPlus,
  Search,
  CheckCircle,
  XCircle,
  Loader2,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";

interface BackOfficeItem {
  id: number;
  username: string;
  email: string;
  role: string;
  status: string;
  is_active: boolean;
  permissions?: string[] | any;
  resolved_permissions?: string[];
  created_at: string;
}

const BACKOFFICE_PERMISSIONS = [
  { id: "USERS_VIEW", label: "View Players / Members", module: "Users" },
  { id: "USERS_EDIT", label: "Edit Players Information", module: "Users" },
  { id: "AGENT_VIEW", label: "View Agents & Stores", module: "Agents" },
  { id: "DEPOSIT_VIEW", label: "View Recharge List", module: "Finance" },
  { id: "DEPOSIT_APPROVE_REJECT", label: "Approve/Reject Recharges", module: "Finance" },
  { id: "WITHDRAWAL_VIEW", label: "View Withdrawal List", module: "Finance" },
  { id: "WITHDRAWAL_APPROVE_REJECT", label: "Approve/Reject Withdrawals", module: "Finance" },
  { id: "BONUS_VIEW", label: "View Bonus and Penalty", module: "Bonuses" },
  { id: "REPORT_VIEW", label: "View Bet History & Reports", module: "Reports" },
];

const BackOfficeManagement: React.FC = () => {
  const { hasPermission, isSuperAdmin } = useAuth();
  const [users, setUsers] = useState<BackOfficeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Add Back Office Modal
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newUser, setNewUser] = useState({
    username: "",
    email: "",
    password: "",
    permissions: ["USERS_VIEW", "AGENT_VIEW", "DEPOSIT_VIEW", "WITHDRAWAL_VIEW"] as string[],
  });
  const [submitting, setSubmitting] = useState(false);

  // Permissions Modal
  const [isPermsModalOpen, setIsPermsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<BackOfficeItem | null>(null);
  const [editPermissions, setEditPermissions] = useState<string[]>([]);

  useEffect(() => {
    fetchBackOfficeList();
  }, [page]);

  const fetchBackOfficeList = async () => {
    setLoading(true);
    try {
      const res = await api.get("/back-office", {
        params: { page, limit: 15, search },
      });
      if (res.data?.success) {
        setUsers(res.data.data.users || []);
        setTotalPages(res.data.data.pagination.totalPages || 1);
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to load back office users");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchBackOfficeList();
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUser.username || !newUser.email || !newUser.password) {
      toast.error("Please fill in username, email, and password");
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post("/back-office", newUser);
      if (res.data?.success) {
        toast.success("Back Office staff created successfully");
        setIsAddModalOpen(false);
        setNewUser({
          username: "",
          email: "",
          password: "",
          permissions: ["USERS_VIEW", "AGENT_VIEW", "DEPOSIT_VIEW", "WITHDRAWAL_VIEW"],
        });
        fetchBackOfficeList();
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to create back office user");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (item: BackOfficeItem) => {
    const newStatus = item.status === "active" ? "disabled" : "active";
    const actionLabel = newStatus === "active" ? "activate" : "disable";

    const confirm = await Swal.fire({
      title: `Are you sure?`,
      text: `Do you want to ${actionLabel} staff account "${item.username}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: newStatus === "active" ? "#10B981" : "#EF4444",
      confirmButtonText: `Yes, ${actionLabel}`,
    });

    if (confirm.isConfirmed) {
      try {
        const res = await api.patch(`/back-office/${item.id}/status`, { status: newStatus });
        if (res.data?.success) {
          toast.success(res.data.message);
          fetchBackOfficeList();
        }
      } catch (err: any) {
        toast.error(err.response?.data?.message || `Failed to ${actionLabel} user`);
      }
    }
  };

  const openPermissionsModal = (item: BackOfficeItem) => {
    setSelectedUser(item);
    let perms: string[] = [];
    if (Array.isArray(item.permissions)) {
      perms = item.permissions;
    } else if (item.resolved_permissions) {
      perms = item.resolved_permissions;
    }
    setEditPermissions(perms);
    setIsPermsModalOpen(true);
  };

  const handleSavePermissions = async () => {
    if (!selectedUser) return;
    setSubmitting(true);
    try {
      const res = await api.put(`/back-office/${selectedUser.id}`, {
        permissions: editPermissions,
      });
      if (res.data?.success) {
        toast.success("Permissions updated successfully");
        setIsPermsModalOpen(false);
        fetchBackOfficeList();
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to update permissions");
    } finally {
      setSubmitting(false);
    }
  };

  const togglePerm = (permId: string) => {
    setEditPermissions((prev) =>
      prev.includes(permId) ? prev.filter((p) => p !== permId) : [...prev, permId]
    );
  };

  const toggleNewUserPerm = (permId: string) => {
    setNewUser((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permId)
        ? prev.permissions.filter((p) => p !== permId)
        : [...prev.permissions, permId],
    }));
  };

  return (
    <div className="space-y-6">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-emerald-50 text-emerald-600 rounded-xl">
            <Briefcase size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Back Office Staff</h1>
            <p className="text-sm text-gray-500">
              Operational staff handling user verifications, deposits, withdrawals, and reporting.
            </p>
          </div>
        </div>

        {(isSuperAdmin || hasPermission("BACKOFFICE_CREATE")) && (
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-xl font-medium transition shadow-sm"
          >
            <UserPlus size={18} />
            <span>Add Back Office Staff</span>
          </button>
        )}
      </div>

      {/* Search */}
      <div className="flex justify-between items-center gap-4">
        <form onSubmit={handleSearch} className="flex-1 max-w-md relative">
          <Search size={18} className="absolute left-3.5 top-3 text-gray-400" />
          <input
            type="text"
            placeholder="Search back office staff..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </form>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-600">
            <thead className="bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              <tr>
                <th className="px-6 py-4">S.No</th>
                <th className="px-6 py-4">Staff Member</th>
                <th className="px-6 py-4">Email</th>
                <th className="px-6 py-4">Role</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Assigned Modules</th>
                <th className="px-6 py-4">Created Date</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-gray-400">
                    <Loader2 className="animate-spin inline-block mr-2" size={20} />
                    Loading back office users...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-gray-400">
                    No back office staff registered yet.
                  </td>
                </tr>
              ) : (
                users.map((item, index) => {
                  const isActive = item.status === "active";
                  return (
                    <tr key={item.id} className="hover:bg-gray-50 transition">
                      <td className="px-6 py-4 font-mono text-xs text-gray-400">
                        #{(page - 1) * 15 + index + 1}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-xs">
                            {item.username.substring(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-semibold text-gray-800">{item.username}</div>
                            <span className="text-xs text-gray-400">UID: #{item.id}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 font-mono text-xs text-gray-700">{item.email}</td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                          BACK_OFFICE
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${isActive
                              ? "bg-emerald-50 text-emerald-700"
                              : "bg-red-50 text-red-700"
                            }`}
                        >
                          {isActive ? (
                            <CheckCircle size={12} className="text-emerald-500" />
                          ) : (
                            <XCircle size={12} className="text-red-500" />
                          )}
                          {item.status || "active"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => openPermissionsModal(item)}
                          className="text-xs font-medium text-emerald-600 hover:text-emerald-800 bg-emerald-50 hover:bg-emerald-100 px-2.5 py-1 rounded-lg transition"
                        >
                          {(item.resolved_permissions || []).length} active permissions
                        </button>
                      </td>
                      <td className="px-6 py-4 text-xs text-gray-500">
                        {new Date(item.created_at || Date.now()).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleToggleStatus(item)}
                            title={isActive ? "Disable Account" : "Enable Account"}
                            className={`p-1.5 rounded-lg transition ${isActive
                                ? "text-gray-400 hover:text-red-600 hover:bg-red-50"
                                : "text-gray-400 hover:text-emerald-600 hover:bg-emerald-50"
                              }`}
                          >
                            {isActive ? <XCircle size={16} /> : <CheckCircle size={16} />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Back Office Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 max-w-xl w-full p-6 space-y-5">
            <div className="flex justify-between items-center border-b pb-4">
              <div className="flex items-center gap-2">
                <Briefcase className="text-emerald-600" size={22} />
                <h3 className="font-bold text-lg text-gray-900">Create Back Office Staff</h3>
              </div>
              <button onClick={() => setIsAddModalOpen(false)} className="text-gray-400 p-1">
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Operations Assistant"
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  required
                  placeholder="staff@taitanbet.com"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Password *
                </label>
                <input
                  type="password"
                  required
                  minLength={6}
                  placeholder="Minimum 6 characters"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">
                  Assigned Operational Permissions
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto pr-1">
                  {BACKOFFICE_PERMISSIONS.map((p) => {
                    const isChecked = newUser.permissions.includes(p.id);
                    return (
                      <label
                        key={p.id}
                        onClick={() => toggleNewUserPerm(p.id)}
                        className={`flex items-center gap-2 p-2 rounded-lg border text-xs cursor-pointer select-none transition ${isChecked
                            ? "bg-emerald-50 border-emerald-300 text-emerald-800"
                            : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
                          }`}
                      >
                        <input type="checkbox" checked={isChecked} onChange={() => { }} />
                        <span>{p.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-5 py-2 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl transition flex items-center gap-2"
                >
                  {submitting && <Loader2 size={16} className="animate-spin" />}
                  Create Staff Account
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Permissions Modal */}
      {isPermsModalOpen && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 max-w-lg w-full p-6 space-y-5">
            <div className="flex justify-between items-center border-b pb-4">
              <div>
                <h3 className="font-bold text-lg text-gray-900">
                  Permissions: {selectedUser.username}
                </h3>
                <p className="text-xs text-gray-500">Configure operational module access.</p>
              </div>
              <button onClick={() => setIsPermsModalOpen(false)} className="text-gray-400 p-1">
                ✕
              </button>
            </div>

            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {BACKOFFICE_PERMISSIONS.map((perm) => {
                const isChecked = editPermissions.includes(perm.id);
                return (
                  <label
                    key={perm.id}
                    onClick={() => togglePerm(perm.id)}
                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer select-none transition ${isChecked
                        ? "bg-emerald-50/70 border-emerald-300 text-emerald-900"
                        : "bg-white border-gray-200 text-gray-700 hover:border-gray-300"
                      }`}
                  >
                    <input type="checkbox" checked={isChecked} onChange={() => { }} />
                    <div>
                      <div className="text-xs font-semibold">{perm.label}</div>
                      <div className="text-[11px] text-gray-400 font-mono">{perm.id}</div>
                    </div>
                  </label>
                );
              })}
            </div>

            <div className="flex justify-between items-center pt-4 border-t">
              <span className="text-xs text-gray-500">
                {editPermissions.length} module(s) selected
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setIsPermsModalOpen(false)}
                  className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSavePermissions}
                  disabled={submitting}
                  className="px-5 py-2 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl transition flex items-center gap-2"
                >
                  {submitting && <Loader2 size={16} className="animate-spin" />}
                  Save Permissions
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BackOfficeManagement;
